import { useEffect, useState } from 'react'
import type { JSX } from 'react/jsx-runtime'
import type { Session, User } from '@supabase/supabase-js'
import { authContext } from '@/features/auth/contexts/auth-context'
import { supabase } from '@/lib/supabase'
import type { AuthProviderProps } from '@/types'

export const AuthProvider = ({ children }: AuthProviderProps): JSX.Element => {
  const [user, setUser] = useState<undefined | User>()
  const [session, setSession] = useState<Session | undefined>()
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    supabase.auth
      .getSession()
      .then(({ data: { session: authSession }, error }) => {
        if (error) {
          console.error('Error getting session:', error.message)
        }
        setSession(authSession ?? undefined)
        setUser(authSession?.user ?? undefined)
        setIsLoading(false)
      })
      .catch((error) => {
        console.error('Error in getSession:', error)
        setIsLoading(false)
      })

    // Listen for changes on auth state (logged in, signed out, etc.)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, authSession) => {
      setSession(authSession ?? undefined)
      setUser(authSession?.user ?? undefined)
      setIsLoading(false)
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  // eslint-disable-next-line unicorn/consistent-function-scoping
  const signOut = async (): Promise<void> => {
    const { error } = await supabase.auth.signOut()

    if (error) {
      console.error('Error signing out:', error.message)
    }
  }

  return (
    <authContext.Provider value={{ isLoading, session, signOut, user }}>
      {children}
    </authContext.Provider>
  )
}
